/**
 * Authentication Service
 * 
 * Handles all authentication-related API calls.
 */

import api from '../utils/api';
import config from '../config';
import { setUserRole, clearUserRole } from '../utils/roleHelpers';

/**
 * Login user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} - Login response with tokens and user data
 */
export const login = async (email, password) => {
  try {
    const response = await api.post('/v1/api/auth/login', {
      email,
      password,
    });

    // Handle successful login
    if (response.status === 'success' && response.data) {
      const { access_token, refresh_token, user } = response.data;

      // Store tokens
      if (access_token) {
        api.setAuthToken(access_token);
      }

      // Store refresh token if provided
      if (refresh_token) {
        localStorage.setItem(config.auth.refreshTokenKey, refresh_token);
      }

      // Store user data
      if (user) {
        localStorage.setItem('user_data', JSON.stringify(user));
        
        // Store user role
        if (user.role) {
          setUserRole(user.role);
        }
      }

      return {
        success: true,
        data: response.data,
        message: response.message || 'Login successful',
        userRole: user?.role || null,
      };
    }

    // Handle error response from API
    if (response.status === 'error') {
      return {
        success: false,
        message: response.message || 'Login failed',
        error: response.error || response.message,
        code: response.statusCode || null,
        response: response,
      };
    }

    // Handle unexpected response format
    return {
      success: false,
      message: response.message || 'Login failed',
      error: 'Unexpected response format',
      response: response,
    };
  } catch (error) {
    // Handle API errors
    let errorMessage = 'An error occurred during login';
    let errorCode = null;
    let errorResponse = null;
    let retryAfter = null;

    // Debug logging (remove in production)
    if (config.features.debugMode) {
      console.log('Login error caught:', {
        hasResponse: !!error.response,
        status: error.status,
        message: error.message,
        name: error.name,
        response: error.response,
      });
    }

    // CRITICAL: Always check for backend response FIRST, even if error name suggests network error
    // This ensures we get the actual backend error message for rate limits, etc.
    if (error.response) {
      // Backend returned an error response - extract the actual error message
      errorResponse = error.response;
      errorCode = error.status || errorResponse.statusCode || null;
      
      // Handle rate limit errors (429) - use error field, not message
      if (error.status === 429 || errorCode === 429) {
        // Get retryAfter from error object (extracted from Retry-After header)
        retryAfter = error.retryAfter || null;
        
        // For rate limit, ALWAYS use error field from backend (it has the detailed message with time)
        errorMessage = errorResponse.error || errorResponse.message || errorMessage;
      } else {
        // For other errors, prioritize error field from backend, then message
        errorMessage = errorResponse.error || errorResponse.message || errorMessage;
      }
    } 
    // SECOND: Check if we have a status code but no response (unlikely but possible)
    else if (error.status) {
      errorCode = error.status;
      if (error.message && error.message !== 'Failed to fetch') {
        errorMessage = error.message;
      }
      
      // Handle rate limit
      if (error.status === 429) {
        retryAfter = error.retryAfter || null;
      }
    }
    // THIRD: Check if it's a network error (ONLY if no response was received)
    // IMPORTANT: Only treat as network error if we truly didn't get a response from the server
    else if (
      !error.response && // No response means it's a network error
      (
        error.message === 'Failed to fetch' ||
        error.message.includes('NetworkError') ||
        (error.name === 'TypeError' && !error.status) ||
        error.isNetworkError
      )
    ) {
      // Network error - provide helpful message
      errorMessage = 'Network error. Please check your internet connection and try again.';
      errorCode = 'NETWORK_ERROR';
    }
    // FOURTH: Use the error message if available
    else if (error.message && error.message !== 'Failed to fetch') {
      errorMessage = error.message;
    }

    // Debug logging for return value
    if (config.features.debugMode) {
      console.log('Login error return:', {
        errorMessage,
        errorCode,
        errorResponse,
        retryAfter,
        errorField: errorResponse?.error,
      });
    }

    return {
      success: false,
      message: errorMessage,
      // For rate limit, ensure we pass the original error field from backend
      // This is the detailed message like "Rate limit exceeded. Please try again in 5 seconds."
      error: errorResponse?.error || errorMessage,
      code: errorCode,
      response: errorResponse,
      retryAfter: retryAfter,
    };
  }
};

/**
 * Logout user
 * Clears all authentication data
 */
export const logout = () => {
  api.removeAuthToken();
  localStorage.removeItem(config.auth.refreshTokenKey);
  clearUserRole();
  localStorage.removeItem('user_data');
};

/**
 * Get current user data from localStorage
 * @returns {Object|null} - User data or null
 */
export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Fetch current user profile from the API
 * @returns {Promise} - Current user response
 */
export const getProfile = async () => {
  try {
    const response = await api.get('/v1/api/auth/me');

    if (response.status === 'success' && response.data) {
      localStorage.setItem('user_data', JSON.stringify(response.data));
      if (response.data.role) {
        setUserRole(response.data.role);
      }

      return {
        success: true,
        data: response.data,
        message: response.message || 'Profile retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve profile',
      data: null,
    };
  } catch (error) {
    console.error('Get profile error:', error);

    let errorMessage = 'Failed to retrieve profile. Please try again.';
    if (error.response) {
      const errorData = error.response;
      errorMessage = errorData.error || errorData.message || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Update current user profile
 * @param {Object} profileData - Profile fields to update
 * @returns {Promise} - Update profile response
 */
export const updateProfile = async (profileData) => {
  try {
    const response = await api.patch('/v1/api/auth/me', profileData);

    if (response.status === 'success' && response.data) {
      localStorage.setItem('user_data', JSON.stringify(response.data));
      if (response.data.role) {
        setUserRole(response.data.role);
      }

      return {
        success: true,
        data: response.data,
        message: response.message || 'Profile updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update profile',
      data: null,
    };
  } catch (error) {
    console.error('Update profile error:', error);

    let errorMessage = 'Failed to update profile. Please try again.';
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message || d.field).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Invite an admin or center admin user.
 * @param {Object} userData - Invitation data
 * @returns {Promise} - Invitation response
 */
export const inviteUser = async (userData) => {
  try {
    const response = await api.post('/v1/api/auth/invite', {
      name: userData.name,
      email: userData.email,
      role: userData.role,
      center: userData.center || undefined,
    });

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Invitation sent successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to send invitation',
      data: null,
    };
  } catch (error) {
    console.error('Invite user error:', error);

    let errorMessage = 'Failed to send invitation. Please try again.';
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message || d.field).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get invitation details from a setup token.
 * @param {string} token - Invitation token
 * @returns {Promise} - Invitation response
 */
export const getInvitation = async (token) => {
  try {
    const response = await api.get('/v1/api/auth/invitation', { token });

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Invitation retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to load invitation',
      data: null,
    };
  } catch (error) {
    console.error('Get invitation error:', error);

    let errorMessage = 'Invitation link is invalid or has expired.';
    if (error.response) {
      const errorData = error.response;
      errorMessage = errorData.error || errorData.message || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Accept invitation and set account password.
 * @param {string} token - Invitation token
 * @param {string} password - New password
 * @returns {Promise} - Accept response
 */
export const acceptInvitation = async (token, password) => {
  try {
    const response = await api.post('/v1/api/auth/invitation/accept', {
      token,
      password,
    });

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Password set successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to set password',
      data: null,
    };
  } catch (error) {
    console.error('Accept invitation error:', error);

    let errorMessage = 'Failed to set password. Please try again.';
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message || d.field).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean} - True if user has valid token
 */
export const isAuthenticated = () => {
  const token = api.getAuthToken();
  const user = getCurrentUser();
  return !!(token && user);
};

/**
 * Register a new user (Admin only)
 * @param {Object} userData - User registration data
 * @param {string} userData.name - User's full name
 * @param {string} userData.email - User's email address
 * @param {string} userData.password - User's password
 * @param {string} userData.role - User role (admin, blogger, or center_admin)
 * @param {string} [userData.center] - Center name (optional, required for center_admin)
 * @returns {Promise} - Registration response
 */
export const register = async (userData) => {
  try {
    const response = await api.post('/v1/api/auth/register', {
      name: userData.name,
      email: userData.email,
      password: userData.password,
      role: userData.role,
      center: userData.center || undefined,
    });

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'User registered successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to register user',
      data: null,
    };
  } catch (error) {
    console.error('Register error:', error);
    
    let errorMessage = 'Failed to register user. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        // Handle validation errors
        const validationErrors = errorData.details.map(d => d.message || d.field).join(', ');
        errorMessage = validationErrors || errorMessage;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

const authService = {
  login,
  logout,
  getCurrentUser,
  getProfile,
  updateProfile,
  inviteUser,
  getInvitation,
  acceptInvitation,
  isAuthenticated,
  register,
};
export default authService;
